/*
 * A commander's production over time, as their state reports it
 * (`economy.history`: a sample every 30 s, the last two hours, oldest
 * first; each sample every good's made and used a minute). Kept in memory
 * on the server, so it starts empty after a restart.
 */

import { createSignal } from "solid-js";
import { oreRGB } from "../render/colour";

export interface Sample {
  tick: number;
  /** By good id: [made a minute, used a minute]. */
  rates: [number, number][];
}

const [samples, setSamples] = createSignal<Sample[]>([]);
const [names, setNames] = createSignal<string[]>([]);
export { samples, names as historyGoods };

export const TICKS_PER_MINUTE = 600;

/** Every good now (the state's `economy.goods`): stock, made and used a
 *  minute, by good id. */
export interface GoodNow {
  good: number;
  stock: number;
  made_per_min: number;
  used_per_min: number;
}
const [current, setCurrent] = createSignal<GoodNow[]>([]);
export { current as goodsNow };

export function setHistory(state: Record<string, unknown>): void {
  const now = (state.economy as { goods?: GoodNow[] } | undefined)?.goods;
  if (now) setCurrent(now);
  const h = (state.economy as { history?: [number, [number, number][]][] } | undefined)?.history;
  if (h) setSamples(h.map(([tick, rates]) => ({ tick, rates })));
  const g = state.goods as string[] | undefined;
  if (g) setNames(g);
}

/** A good's made and used a minute over time (null: no such good). */
export function series(name: string): { tick: number; made: number; used: number }[] | null {
  const k = names().indexOf(name === "Rounds" ? "Munitions" : name);
  if (k < 0) return null;
  return samples().map((s) => ({ tick: s.tick, made: s.rates[k]?.[0] ?? 0, used: s.rates[k]?.[1] ?? 0 }));
}

/** A good's colour: ores (and coal, oil) the atlas's, the rest their own. */
export function goodHue(name: string): string {
  const ore = name === "Coal" || name === "Oil" ? name : name.endsWith(" ore") ? name.replace(/ ore$/, "") : "";
  if (ore) return `rgb(${oreRGB(ore).map((v) => Math.round(v * 255)).join(",")})`;
  return (
    {
      Steel: "#9fb0c2",
      Copper: "#e0905a",
      Fuel: "#d9b44a",
      Munitions: "#ece2c4",
      Rounds: "#ece2c4",
      Recruits: "#8fb8de",
    }[name] ?? "#c9c4b8"
  );
}

/** A per-minute amount, short ("1.2k"). */
export function rate(n: number): string {
  const a = Math.abs(n);
  return a >= 10_000 ? `${Math.round(n / 1000)}k` : a >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`;
}
